import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'

import type { PartnerNames } from '~/api/partner-promo-code/types'

import translations from './telcoPlanComparison.translations'
import type { TelcoPlanComparison } from './types'

/**
 * Display name for EVERY telco partner the picker can surface, including
 * config-less partners (GOOGLE_FI, BESTPHONEPLANS, RAKUTEN) that have no
 * comparison-drawer entry. Used by the picker rows to render a partner name even
 * when there is no full comparison config.
 *
 * Typed as an exhaustive `Record<PartnerNames, …>` so that adding a partner to
 * the backend enum is a compile error here until a display name is provided.
 * VISIBLE/NOBLE reuse the existing comparison name keys.
 */
export const TELCO_PARTNER_DISPLAY_NAMES: Record<PartnerNames, I18nDefinition> =
  {
    VISIBLE: translations.visibleName,
    NOBLE: translations.nobleName,
    GOOGLE_FI: translations.googleFiName,
    BESTPHONEPLANS: translations.bestPhonePlansName,
    RAKUTEN: translations.rakutenName,
    US_MOBILE: translations.usMobileName,
    DUOLINGO: translations.duolingoName,
  }

/**
 * Logo asset paths for telco partner brands. Keyed by PartnerNames.
 * Only partners with a confirmed logo asset are included — Partial intentionally.
 * Reference only existing files under apps/front-office/public/img/.
 */
// TODO(business): confirm exact asset paths for all partners before launch
export const TELCO_PARTNER_LOGO_PATHS: Partial<Record<PartnerNames, string>> = {
  VISIBLE: '/img/visible-by-verizon/visible-by-verizon-logo-square.svg',
  NOBLE: '/img/noble/noble-logo-tiny-white.svg',
  GOOGLE_FI: '/img/telco-partnership/google-fi.svg',
  BESTPHONEPLANS: '/img/bestphoneplans/bestphoneplans-logo-blue.svg',
  RAKUTEN: '/img/rakuten/rakuten-logo-pink.svg',
}

/**
 * Static plan-comparison attributes rendered by the telco comparison drawer.
 * Ordered Visible-first, Noble-second per Figma.
 *
 * Why static: the `GET /partner-promo-codes/api/v1/offers` list response is lean
 * (`id` + `partner.name` only — no price/link, `images` empty in prod, see
 * Sprint 1 Agent Notes), so the picker UI sources all presentational copy from
 * here via i18n keys and matches each option to its config entry by
 * `partnerName`. The backend remains the source of truth for `offerId` only.
 */
export const TELCO_PLAN_COMPARISONS: ReadonlyArray<TelcoPlanComparison> = [
  {
    partnerName: 'VISIBLE',
    name: translations.visibleName,
    network: translations.visibleNetwork,
    data: translations.visibleData,
    hotspot: translations.visibleHotspot,
    price: translations.visiblePrice,
    priceOld: translations.visiblePriceOld,
    priceNew: translations.visiblePriceNew,
    pickerSubLine: translations.visiblePickerSubLine,
    bullets: [
      translations.visibleBullet1,
      translations.visibleBullet2,
      translations.visibleBullet3,
    ],
    offerLine: translations.visibleOfferLine,
  },
  {
    partnerName: 'NOBLE',
    name: translations.nobleName,
    network: translations.nobleNetwork,
    data: translations.nobleData,
    hotspot: translations.nobleHotspot,
    price: translations.noblePrice,
    priceOld: translations.noblePriceOld,
    priceNew: translations.noblePriceNew,
    pickerSubLine: translations.noblePickerSubLine,
    bullets: [
      translations.nobleBullet1,
      translations.nobleBullet2,
      translations.nobleBullet3,
    ],
    offerLine: translations.nobleOfferLine,
  },
]

/**
 * Runtime allow-list of partners that have a full comparison-drawer config entry.
 * Derived from {@link TELCO_PLAN_COMPARISONS} so adding a partner to the drawer
 * config automatically widens picker eligibility — single source of truth.
 *
 * The telco picker only surfaces offers whose `partnerName` is in this set;
 * offers from config-less partners (GOOGLE_FI, BESTPHONEPLANS, RAKUTEN, ...) are
 * silently discarded before eligibility is evaluated. Typed as
 * `ReadonlySet<PartnerNames>` (the wider backend enum) so membership checks
 * against any backend-sourced `partnerName` are type-clean.
 */
export const CONFIGURED_PARTNER_NAMES: ReadonlySet<PartnerNames> = new Set(
  TELCO_PLAN_COMPARISONS.map((comparison) => comparison.partnerName),
)
